// Non-production console sink for Sentry/GlitchTip.
//
// Outside production builds nothing may leave the browser, but error handling
// must stay debuggable: developers (and Claude, reading the dev-server browser
// console) need to see exactly what WOULD have been sent — tags, contexts,
// extra, user, fingerprint — after the whole scope/processor pipeline has run.
//
// Two pieces, wired by init.ts outside production only:
//   - `logAndDropEvent` (beforeSend): logs the fully assembled event to the
//     console, then returns null so the SDK drops it before the transport.
//   - `makeNoopTransport` (transport): swallows every remaining envelope kind
//     (sessions, client reports) as a structural guarantee that zero GlitchTip
//     traffic leaves non-prod even if init options grow later.
//
// Console format (mirrors the `med-analytics [debug]:` logger convention):
//   ▸ glitchtip [debug]: <level> <Type: message> — logged, not sent
//       <pretty-printed JSON summary — the greppable contract>
//       event: <raw event object, for interactive inspection in devtools>
//       originalException: <the thrown value, when there is one>
//
// Only console.groupCollapsed/log here — console.error would be re-captured by
// captureConsoleIntegration and loop.
import type * as Sentry from '@sentry/vue'

const LEVEL_COLORS: Record<string, string> = {
	fatal: '#ef4444',
	error: '#ef4444',
	warning: '#f59e0b',
}

/**
 * JSON.stringify that survives circular references and BigInt — beforeSend runs
 * before the SDK normalises the event, so `extra`/`contexts` may hold anything.
 */
function safeStringify(value: unknown): string {
	const seen = new WeakSet<object>()
	try {
		return JSON.stringify(
			value,
			(_key, val: unknown) => {
				if (typeof val === 'bigint') return String(val)
				if (typeof val === 'object' && val !== null) {
					if (seen.has(val)) return '[circular]'
					seen.add(val)
				}
				return val
			},
			2,
		)
	} catch (stringifyError) {
		return `[unserialisable: ${String(stringifyError)}]`
	}
}

/** Headline for the console group: exception type + message, or the captured message. */
function eventTitle(event: Sentry.Event): string {
	const exception = event.exception?.values?.[0]
	if (exception) return `${exception.type ?? 'Error'}: ${exception.value ?? '(no message)'}`
	return event.message ?? '(no message)'
}

/**
 * `beforeSend` for non-production: log what would have been sent, then return
 * null so the event is dropped before the transport.
 */
export function logAndDropEvent(event: Sentry.Event, hint: Sentry.EventHint): null {
	const level = event.level ?? 'error'
	// Curated summary instead of the raw event: stable, greppable, and free of
	// bulky stack frames / breadcrumb bodies (those live on the raw `event` arg).
	const summary = {
		event_id: event.event_id,
		level,
		environment: event.environment,
		release: event.release,
		transaction: event.transaction,
		message: event.message,
		exception: event.exception?.values?.map((ex) => ({
			type: ex.type,
			value: ex.value,
			mechanism: ex.mechanism?.type,
			handled: ex.mechanism?.handled,
		})),
		tags: event.tags,
		contexts: event.contexts,
		extra: event.extra,
		user: event.user,
		fingerprint: event.fingerprint,
		breadcrumbs_count: event.breadcrumbs?.length ?? 0,
	}

	/* eslint-disable no-console */
	console.groupCollapsed(
		`%cglitchtip [debug]: %c${level} ${eventTitle(event)} %c— logged, not sent`,
		'font-weight: 400; color: #888',
		`color: ${LEVEL_COLORS[level] ?? '#888'}; font-weight: 600`,
		'font-weight: 400; color: #888',
	)
	console.log(safeStringify(summary))
	console.log('event:', event)
	if (hint.originalException != null) console.log('originalException:', hint.originalException)
	console.groupEnd()
	/* eslint-enable no-console */

	return null
}

/**
 * `transport` for non-production: accept and discard every envelope. Error
 * events are already dropped in beforeSend; this catches everything else the
 * SDK emits on its own (sessions, client reports) so nothing reaches GlitchTip.
 *
 * Return type stays inferred — the SDK's `Transport` interface is not
 * re-exported by `@sentry/vue`; the `transport:` option in init.ts checks the
 * shape structurally.
 */
export function makeNoopTransport() {
	return {
		send: () => Promise.resolve({}),
		flush: () => Promise.resolve(true),
	}
}
